import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import Vue from 'vue'

@Module({
  namespaced: true
})
export default class CommunicationStore extends VuexModule {
  modelsLoaded = false
  models: KwixeoCommunication.Models = []
  sendersLoaded = false
  senders: KwixeoCommunication.Senders = []
  mailForms: KwixeoCommunication.OpenedMailForms = []
  reportTypesLoaded = false
  reportTypes: KwixeoCommunication.ReportTypes = []
  mailLabelsLoaded = false
  mailLabels: KwixeoCommunication.MailLabels = []

  // CLEAR STORE STATE
  @Action
  clearStore (): void {
    const elements = ['Models', 'Senders']
    elements.forEach(key => {
      this.context.commit('set' + key, [])
      this.context.commit('set' + key + 'Loaded', false)
    })

    this.context.commit('clearMailForm')
  }

  @Mutation
  setModelsLoaded (state = true): void {
    this.modelsLoaded = state
  }

  @Mutation
  setModels (models: KwixeoCommunication.Models): void {
    this.models = models
  }

  @Mutation
  updateModel (model: KwixeoCommunication.Model): void {
    if (!this.modelsLoaded) {
      return
    }
    const index = this.models.findIndex(cat => cat.id === model.id)
    if (index >= 0) {
      Vue.set(this.models, index, model)
    } else {
      this.models.push(model)
    }
  }

  @Mutation
  removeModel (model: KwixeoCommunication.Model): void {
    if (!this.modelsLoaded) {
      return
    }
    const index = this.models.findIndex(cat => cat.id === model.id)
    if (index !== undefined) {
      Vue.delete(this.models, index)
    }
  }

  @Action
  async loadModels (force = false): Promise<void> {
    if (!this.modelsLoaded || force) {
      const response = await Vue.prototype.$kwCommunication.axios.get('/mail/model', {
        params: {
          per_page: 500,
          sort: 'name:asc'
        }
      })
      await this.context.commit('setModels', response.data.data)
      this.context.commit('setModelsLoaded')
    }
  }

  @Action
  setModel (model: KwixeoCommunication.Model): void {
    this.context.commit('updateModel', model)
  }

  @Action
  deleteModel (model: KwixeoCommunication.Model): void {
    this.context.commit('removeModel', model)
  }

  // Return defaut sender to use or the first
  get defaultSender (): KwixeoCommunication.Sender|undefined {
    const validatedSenders = this.senders.filter(sender => sender.status.active && sender.is_validated)
    const sender = validatedSenders.find(sender => sender.use_default)
    if (sender) {
      return sender
    }
    return validatedSenders.length > 0 ? validatedSenders[1] : undefined
  }

  @Mutation
  setSendersLoaded (state = true): void {
    this.sendersLoaded = state
  }

  @Mutation
  setSenders (senders: KwixeoCommunication.Senders): void {
    this.senders = senders
  }

  @Mutation
  updateSender (sender: KwixeoCommunication.Sender): void {
    if (!this.sendersLoaded) {
      return
    }
    const index = this.senders.findIndex(cat => cat.id === sender.id)
    if (index >= 0) {
      Vue.set(this.senders, index, sender)
    } else {
      this.senders.push(sender)
    }
  }

  @Mutation
  removeSender (sender: KwixeoCommunication.Sender): void {
    if (!this.sendersLoaded) {
      return
    }
    const index = this.senders.findIndex(cat => cat.id === sender.id)
    if (index !== undefined) {
      Vue.delete(this.senders, index)
    }
  }

  @Action
  async loadSenders (force = false): Promise<void> {
    if (!this.sendersLoaded || force) {
      const response = await Vue.prototype.$kwCommunication.axios.get('/mail/sender?per_page=500&with_shared=1')
      await this.context.commit('setSenders', response.data.data)
      this.context.commit('setSendersLoaded')
    }
  }

  @Action
  setSender (sender: KwixeoCommunication.Sender): void {
    this.context.commit('updateSender', sender)
  }

  @Action
  deleteSender (sender: KwixeoCommunication.Sender): void {
    this.context.commit('removeSender', sender)
  }

  // Open a new mail form
  @Mutation
  addMailForm (mail: KwixeoCommunication.OpenedMailForm): void {
    this.mailForms.push(mail)
  }

  // Update an existing mail form
  @Mutation
  updateMailForm (data: { index: number; mail: KwixeoCommunication.OpenedMailForm }): void {
    Vue.set(this.mailForms, data.index, data.mail)
  }

  // Delete a stored mail form
  @Mutation
  removeMailForm (index: number): void {
    this.mailForms.splice(index, 1)
  }

  // Delete all stored mail forms
  @Mutation
  clearMailForm (): void {
    this.mailForms = []
  }

  // Check if some mail form need to be collapsed
  @Mutation
  checkOpenedForm (exclude: number|undefined): void {
    const mails = this.mailForms.map((mail, index) => ({ index, mail }))
    const opens = mails.filter((data) => data.mail.mode === 'normal' && (exclude === undefined || exclude !== data.index))
    const max = exclude === undefined ? 2 : 1
    for (let i = 0; i < (opens.length - max); i++) {
      const mail = JSON.parse(JSON.stringify(opens[i].mail))
      mail.mode = 'collapsed'
      Vue.set(this.mailForms, opens[i].index, mail)
    }
  }

  // open a new mail form
  @Action
  openMailForm (mail: Partial<KwixeoCommunication.OpenedMailForm>): void {
    const mailInit = Object.assign({
      mode: 'normal',
      mail: {}
    }, mail)
    this.context.commit('addMailForm', mailInit)
    this.context.commit('checkOpenedForm')
  }

  // Save an existing mail form
  @Action
  saveMailForm (data: { index: number; mail: KwixeoCommunication.OpenedMailForm }): void {
    const needCheckOpened = this.mailForms[data.index].mode !== data.mail.mode && data.mail.mode === 'normal'
    this.context.commit('updateMailForm', data)
    if (needCheckOpened) {
      this.context.commit('checkOpenedForm', data.index)
    }
  }

  // Close a mail form
  @Action
  closeMailForm (index: number): void {
    this.context.commit('removeMailForm', index)
  }

  @Mutation
  setReportTypesLoaded (state = true): void {
    this.reportTypesLoaded = state
  }

  @Mutation
  setReportTypes (types: KwixeoCommunication.ReportTypes): void {
    this.reportTypes = types
  }

  @Mutation
  updateReportType (type: KwixeoCommunication.ReportType): void {
    if (!this.reportTypesLoaded) {
      return
    }
    const index = this.reportTypes.findIndex(cat => cat.id === type.id)
    if (index >= 0) {
      Vue.set(this.reportTypes, index, type)
    } else {
      this.reportTypes.push(type)
    }
  }

  @Mutation
  removeReportType (type: KwixeoCommunication.ReportType): void {
    if (!this.reportTypesLoaded) {
      return
    }
    const index = this.reportTypes.findIndex(cat => cat.id === type.id)
    if (index !== undefined) {
      Vue.delete(this.reportTypes, index)
    }
  }

  @Action
  async loadReportTypes (force = false): Promise<void> {
    if (!this.reportTypesLoaded || force) {
      const response = await Vue.prototype.$kwCommunication.axios.get('/communication/report/type', {
        params: {
          per_page: 500,
          sort: 'label:asc'
        }
      })
      await this.context.commit('setReportTypes', response.data.data)
      this.context.commit('setReportTypesLoaded')
    }
  }

  @Action
  setReportType (type: KwixeoCommunication.ReportType): void {
    this.context.commit('updateReportType', type)
  }

  @Action
  deleteReportType (type: KwixeoCommunication.ReportType): void {
    this.context.commit('removeReportType', type)
  }


  @Mutation
  setMailLabelsLoaded (state = true): void {
    this.mailLabelsLoaded = state
  }

  @Mutation
  setMailLabels (labels: KwixeoCommunication.MailLabels): void {
    this.mailLabels = labels
  }

  @Mutation
  updateMailLabel (label: KwixeoCommunication.MailLabel): void {
    if (!this.mailLabelsLoaded) {
      return
    }
    const index = this.mailLabels.findIndex(cat => cat.id === label.id)
    if (index >= 0) {
      Vue.set(this.mailLabels, index, label)
    } else {
      this.mailLabels.push(label)
    }
  }

  @Mutation
  removeMailLabel (label: KwixeoCommunication.MailLabel): void {
    if (!this.mailLabels) {
      return
    }
    const index = this.mailLabels.findIndex(cat => cat.id === label.id)
    if (index !== undefined) {
      Vue.delete(this.mailLabels, index)
    }
  }

  @Action
  async loadMailLabels (force = false): Promise<void> {
    if (!this.mailLabelsLoaded || force) {
      const response = await Vue.prototype.$kwCommunication.axios.get('/mail/label', {
        params: {
          per_page: 500,
          sort: 'label:asc'
        }
      })
      await this.context.commit('setMailLabels', response.data.data)
      this.context.commit('setMailLabelsLoaded')
    }
  }

  @Action
  setMailLabel (type: KwixeoCommunication.MailLabel): void {
    this.context.commit('updateMailLabel', type)
  }

  @Action
  deleteMailLabel (type: KwixeoCommunication.MailLabel): void {
    this.context.commit('removeMailLabel', type)
  }

}
